import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTopButton from '@/components/base/BackToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { allMovies, categoryMap } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="text-foreground-500">影片不存在或已下架</p>
            <Link to="/" className="mt-3 inline-block text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = allMovies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.tags.some((t) => movie.tags.includes(t))))
    .slice(0, 6);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.image,
    dateCreated: `${movie.year}`,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.actors.split(', ').map((a) => ({ '@type': 'Person', name: a.trim() })),
    genre: movie.tags,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryMap[movie.category], to: `/category/${movie.category === 'movie' ? 'dianying' : movie.category === 'tv' ? 'dianshiju' : movie.category === 'variety' ? 'zongyi' : movie.category === 'anime' ? 'dongman' : 'jilupian'}` },
                { label: movie.title },
              ]}
            />

            <article className="mt-6 pb-12">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-100 sticky top-24">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      title={`${movie.title} - 午马影院高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {movie.title}
                  </h1>

                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <span className="flex items-center gap-1 text-sm text-accent-500 font-semibold">
                      <i className="ri-star-fill"></i>
                      {movie.rating} 分
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">{categoryMap[movie.category]}</span>
                  </div>

                  <div className="flex gap-1.5 mt-4 flex-wrap">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 space-y-3 text-sm text-foreground-600">
                    <div className="flex gap-2">
                      <span className="text-foreground-400 font-medium whitespace-nowrap">导演：</span>
                      <span>{movie.director}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 font-medium whitespace-nowrap">主演：</span>
                      <span>{movie.actors}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-400 font-medium whitespace-nowrap">更新：</span>
                      <span>{movie.updateInfo}</span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h2 className="text-base font-semibold text-foreground-950 mb-2">剧情简介</h2>
                    <div
                      className="text-sm text-foreground-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: movie.descriptionHtml }}
                    />
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <button className="px-6 py-2.5 rounded-full bg-primary-500 text-white font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                      <i className="ri-play-fill mr-1.5"></i>
                      立即播放
                    </button>
                    <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-heart-line mr-1.5"></i>
                      收藏
                    </button>
                    <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line mr-1.5"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              {relatedMovies.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                    {relatedMovies.map((m, idx) => (
                      <div key={m.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.06}s` }}>
                        <MovieCard movie={m} index={idx} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>

        <Footer />
        <BackToTopButton />
      </div>
    </>
  );
}